import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../config/backend';
import './css/Employee.css';
import { GoogleMap, LoadScript, Marker, Polyline } from '@react-google-maps/api';

const Employee = () => {
    const [employees, setEmployees] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [attendance, setAttendance] = useState([]);
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [showTrackingModal, setShowTrackingModal] = useState(false);
    const [trackingDate, setTrackingDate] = useState('');
    const [trackingData, setTrackingData] = useState(null);
    const [newEmployee, setNewEmployee] = useState({ Name: '', email: '', password: '', Designation: '' });
    const [message, setMessage] = useState('');
    const [attendanceEmployeeId, setAttendanceEmployeeId] = useState(null);

    const token = localStorage.getItem("_crm__token"); // Assumes token is stored here
    const now = new Date();

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const pad = (n) => n.toString().padStart(2, '0');
    const todayFormatted = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const initialStartDate = `${startOfMonth.getFullYear()}-${pad(startOfMonth.getMonth() + 1)}-${pad(startOfMonth.getDate())}`;
    const initialEndDate = `${endOfMonth.getFullYear()}-${pad(endOfMonth.getMonth() + 1)}-${pad(endOfMonth.getDate())}`;
    const [dateRange, setDateRange] = useState({
        startDate: initialStartDate,
        endDate: initialEndDate,
    });


    // Fetch all employees
    const fetchEmployees = async () => {
        try {
            const res = await axios.get(`${api}/Employee/get`);
            if (res.data.success) {
                setEmployees(res.data.employees);
            }
        } catch (error) {
            setMessage('Failed to fetch employees');
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    // Handle input change for new employee form
    const handleInputChange = (e) => {
        setNewEmployee({ ...newEmployee, [e.target.name]: e.target.value });
    };

    // Create new employee
    const handleCreateEmployee = async (e) => {
        e.preventDefault();
        setMessage('');
        try {
            const res = await axios.post(`${api}/Employee/create`, {
                admintoken: token,
                ...newEmployee
            });
            if (res.data.success) {
                setMessage('Employee created successfully');
                setShowCreateModal(false);
                setNewEmployee({ Name: '', email: '', password: '', Designation: '' });
                fetchEmployees();
            } else {
                setMessage(res.data.message || 'Failed to create employee');
            }
        } catch (error) {
            setMessage('Error creating employee');
        }
    };

    // View individual employee details
    const handleViewEmployee = async (id) => {
        setMessage('');
        try {
            const res = await axios.post(`${api}/Employee/getById`, { token, id });
            if (res.data.success) {
                setSelectedEmployee(res.data.Data);
                setShowViewModal(true);
            } else {
                setMessage('Failed to fetch employee details');
            }
        } catch (error) {
            setMessage('Error fetching employee details');
        }
    };

    // Fetch attendance for selected employee
    const handleViewAttendance = async (employeeId) => {
        setMessage('');
        setAttendance([]);
        setAttendanceEmployeeId(employeeId);

        // Reset to this month's dates every time you open the modal
        setDateRange({
            startDate: initialStartDate,
            endDate: initialEndDate,
        });

        setShowAttendanceModal(true);

        try {
            const res = await axios.post(`${api}/Employee/getAttendencebymonth`, {
                token,
                startDate: initialStartDate,
                enddate: initialEndDate,
                id: employeeId
            });
            if (res.data.success) {
                setAttendance(res.data.data);
            } else {
                setMessage('Failed to fetch attendance');
            }
        } catch (error) {
            setMessage('Error fetching attendance');
        }
    };


    // Fetch attendance with date range
    const fetchAttendanceByDate = async () => {
        setMessage('');
        try {
            const res = await axios.post(`${api}/Employee/getAttendencebymonth`, {
                token,
                startDate: dateRange.startDate,
                enddate: dateRange.endDate,
                id: attendanceEmployeeId
            });
            if (res.data.success) {
                setAttendance(res.data.data);
            } else {
                setMessage('Failed to fetch attendance');
            }
        } catch (error) {
            setMessage('Error fetching attendance');
        }
    };

    // Handle date change
    const handleDateChange = (e) => {
        setDateRange({ ...dateRange, [e.target.name]: e.target.value });
    };

    // Fetch tracking data for a specific date
    const fetchTrackingData = async (employeeId, date) => {
        setMessage('');
        try {
            const res = await axios.post(`${api}/Employee/getAttendencebymonth`, {
                token,
                startDate: date,
                enddate: date,
                id: employeeId
            });

            if (res.data.success && res.data.data.length > 0) {
                setTrackingData(res.data.data[0]);
            } else {
                setMessage('No tracking data found for this date');
                setTrackingData(null);
            }
        } catch (error) {
            setMessage('Error fetching tracking data');
            setTrackingData(null);
        }
    };

    // Handle tracking button click
    const handleViewTracking = (employeeId) => {
        setAttendanceEmployeeId(employeeId);
        setTrackingDate(todayFormatted);
        setShowTrackingModal(true);
        fetchTrackingData(employeeId, todayFormatted);
    };

    // Handle date change for tracking
    const handleTrackingDateChange = (e) => {
        const newDate = e.target.value;
        setTrackingDate(newDate);
        fetchTrackingData(attendanceEmployeeId, newDate);
    };


    return (
        <div className="employee-module-container">
            <div className="Category_mainHeader">
                <h2>Employees</h2>
                <button className="add-employee-btn" onClick={() => setShowCreateModal(true)}>
                    Add Employee
                </button>
            </div>
            {message && <div className="message">{message}</div>}
            <table className="employee-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Designation</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {employees.map((emp) => (
                        <tr key={emp._id}>
                            <td>{emp.Name}</td>
                            <td>{emp.email}</td>
                            <td>{emp.Designation}</td>
                            <td>
                                <button className="action-btn view-btn" onClick={() => handleViewEmployee(emp._id)} title="View Details">
                                    View
                                </button>
                                <button className="action-btn attendance-btn" onClick={() => handleViewAttendance(emp._id)} title="View Attendance">
                                    Attendance
                                </button>
                                <button className="action-btn tracking-btn" onClick={() => handleViewTracking(emp._id)} title="View Tracking">
                                    Tracking
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Create Employee Modal */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal-card">
                        <div className="modal-header">
                            <h3>Add Employee</h3>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <form className="modal-body" onSubmit={handleCreateEmployee}>
                            <label>Name</label>
                            <input type="text" name="Name" value={newEmployee.Name} onChange={handleInputChange} required />
                            <label>Email</label>
                            <input type="email" name="email" value={newEmployee.email} onChange={handleInputChange} required />
                            <label>Password</label>
                            <input type="password" name="password" value={newEmployee.password} onChange={handleInputChange} required />
                            <label>Designation</label>
                            <input type="text" name="Designation" value={newEmployee.Designation} onChange={handleInputChange} required />
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-submit">
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Employee Modal */}
            {showViewModal && selectedEmployee && (
                <div className="modal-overlay">
                    <div className="modal-card">
                        <div className="modal-header">
                            <h3>Employee Details</h3>
                            <button className="modal-close" onClick={() => setShowViewModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p><strong>Name:</strong> {selectedEmployee.Name}</p>
                            <p><strong>Email:</strong> {selectedEmployee.email}</p>
                            <p><strong>Designation:</strong> {selectedEmployee.Designation}</p>
                            <p><strong>Status:</strong> {selectedEmployee.Status ? 'Active' : 'Inactive'}</p>
                            <p><strong>Created At:</strong> {new Date(selectedEmployee.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowViewModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tracking Modal */}
            {showTrackingModal && (
                <div className="modal-overlay">
                    <div className="modal-card tracking-modal">
                        <div className="modal-header">
                            <h3>Employee Tracking</h3>
                            <button className="modal-close" onClick={() => setShowTrackingModal(false)}>&times;</button>
                        </div>
                        <div className="scrollable-container">
                            <div className="modal-body tracking-modal-body">
                                <div className="date-picker-single">
                                    <label>Select Date:</label>
                                    <input
                                        type="date"
                                        value={trackingDate}
                                        onChange={handleTrackingDateChange}
                                    />
                                </div>

                                {trackingData ? (
                                    <div className="map-container">
                                        <LoadScript googleMapsApiKey="AIzaSyAW-xZdNAskSJteiT9GmKfnFYRqfeZcWkc">
                                            <GoogleMap
                                                mapContainerStyle={{
                                                    width: '100%',
                                                    height: '400px'
                                                }}
                                                center={{
                                                    lat: parseFloat(trackingData.CheckIn_latitude || trackingData.Tracking[0]?.latitude || 0),
                                                    lng: parseFloat(trackingData.CheckIn_longitude || trackingData.Tracking[0]?.longitude || 0)
                                                }}
                                                zoom={15}
                                            >
                                                {trackingData.Tracking.map((track, index) => (
                                                    <Marker
                                                        key={track._id}
                                                        position={{
                                                            lat: parseFloat(track.latitude),
                                                            lng: parseFloat(track.longitude)
                                                        }}
                                                        label={{
                                                            text: (index + 1).toString(),
                                                            color: 'white'
                                                        }}
                                                        title={`${track.tracktype} - ${track.location}`}
                                                    />
                                                ))}

                                                {trackingData.Tracking.length > 1 && (
                                                    <Polyline
                                                        path={trackingData.Tracking.map(track => ({
                                                            lat: parseFloat(track.latitude),
                                                            lng: parseFloat(track.longitude)
                                                        }))}
                                                        options={{
                                                            strokeColor: '#007bff',
                                                            strokeOpacity: 0.8,
                                                            strokeWeight: 3,
                                                        }}
                                                    />
                                                )}
                                            </GoogleMap>
                                        </LoadScript>

                                        <div className="tracking-details">
                                            <h4>Tracking Details</h4>
                                            <table className="tracking-table">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Type</th>
                                                        <th>Time</th>
                                                        <th>Location</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {trackingData.Tracking.map((track, index) => (
                                                        <tr key={track._id}>
                                                            <td>{index + 1}</td>
                                                            <td>{track.tracktype}</td>
                                                            <td>
                                                                {track.tracktype === 'CheckIn'
                                                                    ? trackingData.CheckIn_Time
                                                                    : track.tracktype === 'CheckOut'
                                                                        ? trackingData.CheckOut_Time
                                                                        : '-'}
                                                            </td>
                                                            <td className="location-cell">{track.location}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no-data-message">
                                        No tracking data available for this date.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowTrackingModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Attendance Modal */}
            {showAttendanceModal && (
                <div className="modal-overlay">
                    <div className="modal-card attendance-modal">
                        <div className="modal-header">
                            <h3>Attendance</h3>
                            <button className="modal-close" onClick={() => setShowAttendanceModal(false)}>
                                &times;
                            </button>
                        </div>
                        <div className="modal-body attendance-modal-body">
                            <div className="date-picker-group">
                                <label>Start Date:</label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={dateRange.startDate}
                                    onChange={handleDateChange}
                                />
                                <label>End Date:</label>
                                <input
                                    type="date"
                                    name="endDate"
                                    value={dateRange.endDate}
                                    onChange={handleDateChange}
                                />
                                <button className="btn-submit" onClick={fetchAttendanceByDate}>
                                    Filter
                                </button>
                            </div>
                            <div className="scrollable-container">
                                <div className="attendance-table-container">
                                    <table className="attendance-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>CheckIn Time</th>
                                                <th>CheckOut Time</th>
                                                <th>Location</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {attendance.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" style={{ textAlign: 'center' }}>
                                                        No attendance records found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                attendance.map((att) => (
                                                    <tr key={att._id}>
                                                        <td>{new Date(att.Date).toLocaleDateString()}</td>
                                                        <td>{att.type}</td>
                                                        <td>{att.CheckIn_Time || '-'}</td>
                                                        <td>{att.CheckOut_Time || '-'}</td>
                                                        <td className="location-cell">
                                                            {att.CheckIn_location || att.CheckOut_location || '-'}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowAttendanceModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Employee;
